import styled from 'styled-components/macro';

import { getBoxShadow, popinEnlarge } from '../../../lib/Utils';

interface MyTeamProps {
  elevation?: 1 | 2 | 3 | 4 | 5;
}

interface ImageProps {
  image: string;
}

interface CellHighlightProps {
  highlight?: boolean;
  dropable?: boolean;
  none?: boolean;
}

export const MyTeamHeader = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  font-style: oblique;
  align-items: baseline;
  padding-top: 2rem;
  padding-left: 1rem;
  padding-right: 1rem;
`;

export const TeamName = styled.div`
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-weight: ${({ theme }) => theme.fontWeights.black};
  color: ${({ theme }) => theme.primary.main};
  font-size: 2.4rem;

  @media (max-width: ${({ theme }) => theme.device.mobileL}px) {
    font-size: 1.6rem;
  }
`;

export const TeamTotal = styled.div`
  color: ${({ theme }) => theme.primary.dark};
  font-family: ${({ theme }) => theme.fontFamily.body};
  font-weight: ${({ theme }) => theme.fontWeights.bold};

  @media (max-width: ${({ theme }) => theme.device.laptop}px) {
    font-size: 1.4rem;
  }
  @media (max-width: ${({ theme }) => theme.device.mobileL}px) {
    font-size: 1.2rem;
  }
`;

export const Table = styled.div`
  display: table;
  font-size: calc(${({ theme }) => theme.fonts.input}rem + 0.42vw);
  width: 100%;
  border-collapse: separate;
  padding-bottom: 2rem;
`;

export const TableHeaders = styled.div`
  display: table-header-group;
`;

export const TableHeader = styled.div<CellHighlightProps>`
  display: table-cell;
  text-align: center;
  color: ${({ theme }) => theme.primary.main};
  font-family: 'Sarabun', sans-serif;
  font-size: calc(0.9rem + 0.3vw);
  font-weight: ${({ theme }) => theme.fontWeights.light};
  line-height: calc((0.9rem + 0.5vw) * 1.3);
  padding: 1vw 0 0.75vw 0;
  text-transform: capitalize;

  @media (max-width: ${({ theme }) => theme.device.laptop}px) {
    display: ${props => (props.dropable ? 'none' : 'table-cell')};
  }
`;

export const TableBody = styled.div`
  color: #515151;
  display: table-row-group;
`;

export const TableRow = styled.div<CellHighlightProps>`
  display: table-row;
  background: ${props => (props.highlight ? props.theme.backgrounds.light : 'white')};
`;

export const TableCell = styled.div<CellHighlightProps>`
  display: table-cell;
  text-align: center;
  font-size: 88%;
  min-width: 6rem;
  vertical-align: middle;
  font-weight: ${({ theme }) => theme.fontWeights.light};
  color: ${props => (props.none ? '#FF5E86' : '#515151')};

  &:first-child {
    border-top-left-radius: ${props => (props.highlight ? '100rem' : 0)};
    border-bottom-left-radius: ${props => (props.highlight ? '100rem' : 0)};
    padding-left: 1rem;
  }

  &:last-child {
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    border-top-right-radius: ${props => (props.highlight ? '100rem' : 0)};
    border-bottom-right-radius: ${props => (props.highlight ? '100rem' : 0)};
  }

  @media (max-width: ${({ theme }) => theme.device.laptop}px) {
    display: ${props => (props.dropable ? 'none' : 'table-cell')};
  }
`;

export const ProfileCell = styled.div`
  display: flex;
  align-items: center;
`;

export const Rank = styled.div`
  font-weight: ${({ theme }) => theme.fontWeights.light};
  font-family: 'Sarabun', sans-serif;
  color: black;
`;

export const ProfileCaptain = styled.div`
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  font-family: 'Sarabun', sans-serif;
  opacity: 0.4;
  margin-left: 0.5rem;
`;

export const ProfileName = styled.div`
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  font-family: 'Sarabun', sans-serif;
  white-space: nowrap;
`;

export const ProfilePic = styled.div<ImageProps>`
  height: 2.2rem;
  width: 2.2rem;
  border-radius: 50%;
  overflow: hidden;
  margin: 0.5rem;
  margin-left: 1rem;
  margin-right: 1rem;
  background: ${props => `url('${props.image}') no-repeat`};
  background-position: center;
  background-size: cover;
`;

export const MyTeamContainer = styled.div<MyTeamProps>`
  width: 100%;
  border-radius: 1.5rem;
  box-shadow: ${({ elevation = 1, theme }) => getBoxShadow(elevation, theme)};
  padding: 1.5rem;
  padding-top: 2rem;
  margin-bottom: 2rem;
  background: white;
  z-index: 20;
  position: relative;
  transform: scale(0);
  animation: ${popinEnlarge} 0.8s cubic-bezier(0.4, 0.01, 0, 1.08) 0.6s forwards;

  @media (max-width: ${({ theme }) => theme.device.tablet}px) {
    margin-left: 0;
  }

  @media (max-width: ${({ theme }) => theme.device.mobileL}px) {
    border-radius: 0;
    margin-top: 1rem;
  }
`;
